import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  collection,
  onSnapshot,
  query,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { CUSTOMER_TAG, CUSTOMER_TAG_THRESHOLDS } from '../../utils/constants';

const CustomerContext = createContext();

export const useCustomers = () => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomers must be used within CustomerProvider');
  }
  return context;
};

export const CustomerProvider = ({ children }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Subscribe to all orders to build customer data
  useEffect(() => {
    const q = query(collection(db, 'orders'), orderBy('timestamp', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ordersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Build customer data from orders
      const customerMap = new Map();

      ordersData.forEach(order => {
        const phone = order.customer?.phone;
        if (!phone) return;

        if (customerMap.has(phone)) {
          const customer = customerMap.get(phone);
          customer.orders.push(order);
          customer.totalOrders += 1;
          if (order.status !== 'cancelled') {
            customer.totalSpent += order.pricing?.total || 0;
          }
          if (order.timestamp > customer.lastOrderDate) {
            customer.lastOrderDate = order.timestamp;
          }
        } else {
          customerMap.set(phone, {
            phone: phone,
            name: order.customer?.name || 'Unknown',
            location: order.customer?.location || '',
            address: order.customer?.address || '',
            orders: [order],
            totalOrders: 1,
            totalSpent: order.status !== 'cancelled' ? (order.pricing?.total || 0) : 0,
            firstOrderDate: order.timestamp,
            lastOrderDate: order.timestamp,
          });
        }
      });

      // Convert map to array and classify customers
      const customersArray = Array.from(customerMap.values()).map(customer => {
        const avgOrderValue = customer.totalOrders > 0
          ? customer.totalSpent / customer.totalOrders
          : 0;

        // Classify customer using constants
        let tag = CUSTOMER_TAG.NEW;
        if (customer.totalOrders >= CUSTOMER_TAG_THRESHOLDS.VIP_ORDERS ||
            customer.totalSpent >= CUSTOMER_TAG_THRESHOLDS.VIP_SPENT) {
          tag = CUSTOMER_TAG.VIP;
        } else if (customer.totalOrders >= CUSTOMER_TAG_THRESHOLDS.REGULAR_ORDERS) {
          tag = CUSTOMER_TAG.REGULAR;
        }

        // Get favorite items with null safety
        const itemCounts = {};
        customer.orders.forEach(order => {
          order.items?.forEach(item => {
            // Check if item and item.name exist to prevent crashes
            if (item && item.name && typeof item.quantity === 'number') {
              itemCounts[item.name] = (itemCounts[item.name] || 0) + item.quantity;
            }
          });
        });
        const favoriteItems = Object.entries(itemCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([name]) => name);

        return {
          ...customer,
          avgOrderValue,
          tag,
          favoriteItems,
          id: customer.phone // Use phone as ID
        };
      });

      // Sort by total spent (descending)
      customersArray.sort((a, b) => b.totalSpent - a.totalSpent);

      setCustomers(customersArray);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching customers:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Get customer by phone
  const getCustomerByPhone = (phone) => {
    return customers.find(c => c.phone === phone);
  };

  // Get customers by tag
  const getCustomersByTag = (tag) => {
    return customers.filter(c => c.tag === tag);
  };

  // Get stats using constants
  const getStats = () => {
    const totalCustomers = customers.length;
    const newCustomers = customers.filter(c => c.tag === CUSTOMER_TAG.NEW).length;
    const regularCustomers = customers.filter(c => c.tag === CUSTOMER_TAG.REGULAR).length;
    const vipCustomers = customers.filter(c => c.tag === CUSTOMER_TAG.VIP).length;
    const totalRevenue = customers.reduce((sum, c) => sum + c.totalSpent, 0);
    const avgOrderValue = customers.length > 0
      ? customers.reduce((sum, c) => sum + c.avgOrderValue, 0) / customers.length
      : 0;

    return {
      totalCustomers,
      newCustomers,
      regularCustomers,
      vipCustomers,
      totalRevenue,
      avgOrderValue
    };
  };

  // Search customers with null safety
  const searchCustomers = (searchTerm) => {
    if (!searchTerm) return customers;

    const term = searchTerm.toLowerCase();
    return customers.filter(c => {
      const name = (c.name || '').toLowerCase();
      const phone = c.phone || '';
      const location = (c.location || '').toLowerCase();

      return name.includes(term) ||
             phone.includes(term) ||
             location.includes(term);
    });
  };

  const value = {
    customers,
    loading,
    getCustomerByPhone,
    getCustomersByTag,
    getStats,
    searchCustomers
  };

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
};
